import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Check, ChevronLeft, ChevronRight, Link2, ListChecks, Rocket, Sigma, Target, CalendarClock,
} from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { cn } from '@/lib/utils'
import type { SurveyResponseDto } from '@/shared/api/types'
import { getPoll, listGroups, listPolls } from './api'
import { PollSelect } from './PollSelect'
import { BuilderPage } from './BuilderPage'
import { ConfigScopeStep } from './ConfigScopeStep'
import { ConfigSampleStep } from './ConfigSampleStep'
import { ConfigLinksStep } from './ConfigLinksStep'
import { ConfigScheduleStep } from './ConfigScheduleStep'
import { ConfigReviewStep } from './ConfigReviewStep'

/**
 * The step-wise configurator.
 *
 * ## What it replaces
 *
 * Creating a poll used to be a four-field modal — title, category, method,
 * scope, target — after which everything else was scattered across five screens
 * and eleven dialogs, with no order and no explanation of what any choice
 * implied. Somebody who picked `CANDIDATE` had no way to learn from the form
 * that it meant they should link candidates, and somebody who picked `TRACKER`
 * had no way to learn that a poll in no tracker cannot be trended.
 *
 * This walks the same decisions in the order they actually depend on each
 * other, and every consequential choice carries a sentence about what follows
 * from it.
 *
 * ## The instrument step IS the Builder
 *
 * Not a summary that links to it, and not a reimplementation of it. `BuilderPage`
 * is mounted with `embeddedPollId`, which suppresses its own title and poll
 * picker and leaves everything else — the version selector, the branch preview,
 * the published-version 409 and the "create version N+1" offer that answers it —
 * exactly as it is on its own route.
 *
 * That matters because question authoring is version-aware: a published version
 * is immutable, and hiding that is how the original product deleted its own
 * history on every edit. Rebuilding a wizard-flavoured editor beside the real
 * one would have been two editors for one instrument, which is the same mistake
 * one level up.
 *
 * ## The steps are not gated
 *
 * A reader may click any step at any time. Sequencing is the recommendation the
 * numbering makes; it is not a lock, because a lock on step 4 is indistinguish-
 * able from a broken step 4 — the same rule that governs every control in this
 * feature. Only "the poll does not exist yet" genuinely blocks the later steps,
 * and it says so.
 */

interface Step {
  key: string
  label: string
  hint: string
  icon: typeof Target
}

/**
 * Links come BEFORE questions, and that ordering is load-bearing.
 *
 * A ballot question's options point at candidates; a service question points at
 * a programme. The picker that sets those pointers offers the poll's DECLARED
 * links, so the list is empty until step 3 has been done — which is the whole
 * reason step 3 is third. The first arrangement of this wizard had questions
 * before links, and a question author reaching for a candidate found nothing to
 * reach for.
 */
const STEPS: Step[] = [
  { key: 'scope', label: 'Purpose & scope', hint: 'What this poll is for, and where', icon: Target },
  { key: 'sample', label: 'Sample & quotas', hint: 'How many, and how precise', icon: Sigma },
  { key: 'links', label: 'Cross-module links', hint: 'Candidates, programmes, places', icon: Link2 },
  { key: 'instrument', label: 'Questions', hint: 'The instrument, its branches and its versions', icon: ListChecks },
  { key: 'schedule', label: 'Schedule', hint: 'The window, and the next wave', icon: CalendarClock },
  { key: 'review', label: 'Review', hint: 'Everything, before it is submitted', icon: Rocket },
]

/** Indexing a tuple returns `T | undefined` under this project's tsconfig. */
const FIRST_STEP: Step = STEPS[0]!

export function ConfiguratorPage() {
  const canRead = useCan('survey:read')
  const canCreate = useCan('survey:create')
  const canUpdate = useCan('survey:update')

  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const stepKey = params.get('step') ?? FIRST_STEP.key
  const found = STEPS.findIndex((s) => s.key === stepKey)
  const stepIndex = found < 0 ? 0 : found
  const step = STEPS[stepIndex] ?? FIRST_STEP
  const previous = stepIndex > 0 ? STEPS[stepIndex - 1] : null
  const next = stepIndex < STEPS.length - 1 ? STEPS[stepIndex + 1] : null

  const [poll, setPoll] = useState<SurveyResponseDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const groups = useLoad(() => listGroups(), [])
  // The picker's own list. Every per-poll screen in this module carries one, and
  // the layout carries the query string between them — so choosing a poll here
  // keeps it chosen on Fieldwork, Quality and Results.
  const polls = useLoad(() => listPolls({}, 0, 200), [])

  const load = useCallback(async (id: string) => {
    try {
      setPoll(await getPoll(id))
      setError(null)
    } catch {
      setError('That poll could not be read. It may have been deleted, or it may be outside '
        + 'your geographic scope.')
      setPoll(null)
    }
  }, [])

  useEffect(() => {
    if (!pollId) { setPoll(null); return }
    void load(pollId)
  }, [pollId, load])

  const goto = (key: string, id: string | null = pollId) => {
    const query = new URLSearchParams(params)
    query.set('step', key)
    if (id) query.set('poll', id)
    else query.delete('poll')
    // The embedded Builder reads `version` from this same query string. Changing
    // poll while a version of the PREVIOUS one is named there would load that
    // poll's instrument under this poll's header — the one thing the Builder's
    // own `set()` already guards against, and the guard has to hold here too now
    // that the step is mounted inside this page.
    if (id !== pollId) query.delete('version')
    setParams(query, { replace: true })
  }

  /** After step 1 saves, the poll id becomes the URL's — so a refresh resumes here. */
  const onSaved = (saved: SurveyResponseDto) => {
    setPoll(saved)
    if (saved.id !== pollId) goto(stepKey, saved.id)
  }

  if (!canRead) return <Forbidden what="the Polling Studio" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Configure a poll"
        description="The decisions a poll needs before fieldwork, in the order they depend on each other"
        right={(
          <div className="flex items-center gap-2">
            <PollSelect
              polls={polls.data?.content ?? []}
              value={pollId}
              onChange={(id) => goto(stepKey, id || null)}
            />
            {canCreate && (
              <button type="button" className="erp-btn-ghost h-8 text-erp-xs"
                onClick={() => {
                  const next = new URLSearchParams(params)
                  next.delete('poll')
                  next.delete('version')
                  next.set('step', 'scope')
                  setParams(next)
                  setPoll(null)
                }}>
                Start a new poll
              </button>
            )}
          </div>
        )}
      />

      <ErrorStrip message={error} />

      <ol className="axis-table-scroll flex items-stretch gap-1 border border-border rounded-md p-1">
        {STEPS.map((entry, index) => {
          const active = index === stepIndex
          const reachable = index === 0 || !!poll
          const Icon = entry.icon
          return (
            <li key={entry.key} className="min-w-max flex-1">
              <button
                type="button"
                disabled={!reachable}
                aria-current={active ? 'step' : undefined}
                onClick={() => goto(entry.key)}
                title={reachable ? entry.hint : 'Create the poll on step 1 first'}
                className={cn(
                  'w-full text-left px-3 py-2 rounded transition-colors disabled:opacity-40',
                  active ? 'bg-chocolate text-white' : 'hover:bg-muted/60',
                )}
              >
                <span className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-erp-xs font-medium whitespace-nowrap">
                    {index + 1}. {entry.label}
                  </span>
                  {index < stepIndex && poll && (
                    <Check className={cn('h-3 w-3 shrink-0',
                      active ? 'text-white' : 'text-emerald-600')} />
                  )}
                </span>
                <span className={cn('block text-erp-xs mt-0.5 whitespace-nowrap',
                  active ? 'text-white/80' : 'text-muted-foreground')}>
                  {entry.hint}
                </span>
              </button>
            </li>
          )
        })}
      </ol>

      {stepKey === 'scope' && (
        <ConfigScopeStep poll={poll} groups={groups.data ?? []} onSaved={onSaved} />
      )}

      {stepKey !== 'scope' && !poll && (
        <Panel title={step.label}>
          <Note>
            This step needs a poll. Pick one above, or create one on step 1 — every step after
            the first is keyed on it: the sample verdict reads the poll's declared frame, the
            link picker is narrowed by its scope, and the schedule hangs off its tracker.
          </Note>
        </Panel>
      )}

      {poll && stepKey === 'sample' && (
        <ConfigSampleStep poll={poll} onSaved={setPoll} />
      )}

      {poll && stepKey === 'instrument' && (
        <BuilderPage embeddedPollId={poll.id} />
      )}

      {poll && stepKey === 'links' && (
        <ConfigLinksStep poll={poll} canUpdate={canUpdate} />
      )}

      {poll && stepKey === 'schedule' && (
        <ConfigScheduleStep poll={poll} canUpdate={canUpdate} canCreate={canCreate}
          onSaved={setPoll} />
      )}

      {poll && stepKey === 'review' && (
        <ConfigReviewStep poll={poll} canUpdate={canUpdate}
          onMoved={() => { if (pollId) void load(pollId) }} />
      )}

      <div className="flex items-center justify-between gap-3">
        <button type="button" className="erp-btn-ghost h-9 text-erp-sm"
          disabled={!previous}
          onClick={() => previous && goto(previous.key)}>
          <ChevronLeft className="h-3.5 w-3.5 mr-1 inline" />
          {previous ? previous.label : 'Back'}
        </button>
        <button type="button" className="erp-btn-primary h-9 text-erp-sm"
          disabled={!next || !poll}
          onClick={() => next && goto(next.key)}>
          {next ? next.label : 'Done'}
          <ChevronRight className="h-3.5 w-3.5 ml-1 inline" />
        </button>
      </div>
    </div>
  )
}
